<template>
  <component :is="tag">
    <!-- content -->
    <div 
      class="fts-popper" 
      :class="[{'append-to-body': appendToBody}, referenceFor]"
      ref="content"
    >
      <transition :name="transition" :enter-active-class="enterActiveClass" :leave-active-class="leaveActiveClass" @after-leave="doDestroy">
        <div 
          class="popper-content fts-scrollbar" 
          :class="contentClass"
          tabindex="0"
          @click="clickHandler"
          ref="popperContent"
          v-if="showPopper"
        >
          <slot name="content" :select="select">{{content}}</slot>
        </div>
      </transition>
    </div>
    <!-- reference -->
    <slot name="reference"></slot>
  </component>
</template>

<script>
import {createPopper} from '@popperjs/core';
import commons from '../commons/commons';

function on(element, event, handler) {
  if (element && event && handler) {
    document.addEventListener ? element.addEventListener(event, handler, false) : element.attachEvent('on' + event, handler);
  }
}
function off(element, event, handler) {
  if (element && event) {
    document.removeEventListener ? element.removeEventListener(event, handler, false) : element.detachEvent('on' + event, handler)
  }
}

export default {
  props: { 
    tag: {
      type: String,
      default: 'div'
    },
    appendToBody: Boolean,
    trigger: {
      type: String,
      default: 'clickToToggle',
      validator: value => [
        'clickToOpen',
        'click', /* same as clickToToggle, provided for backwards compatibility. */
        'clickToToggle',
        'hover',
        'focus'
      ].indexOf(value) > -1
    },
    content: String,  
    /* placement */
    placement: {
      type: String,
      default: 'bottom'
    },
    /* strategy */
    strategyFixed: Boolean,
    /* prevent overflow */
    preventOverflowAltAxis: Boolean,
    preventOverflowTetherOff: Boolean,
    preventOverflowBoundary: String,
    preventOverflowPadding: [Number, Object],
    /* flip */
    flip: {
      type: Boolean,
      default: true,
    },
    /* offset */
    offset: {
      type: [Array, Function],
      default: () => [0, 0]
    },
    /* mouse */
    delayOnMouseOver: {
      type: Number,
      default: 10,
    },
    delayOnMouseOut: {
      type: Number,
      default: 10,
    },
    /* disabled */
    disabled: Boolean,
    /* transition */
    transition: {
      type: String,
      default: ''
    },
    enterActiveClass: String,
    leaveActiveClass: String,
    /* style / class */
    followWidth: Boolean, // content follow trigger element width
    contentClass: String,
    zIndex: Number,
    /* event */
    stopPropagation: Boolean,
    preventDefault: Boolean,
    /* escape handler use */
    referenceId: String,
    referenceFor: String,
    overlay: Boolean, /* works with appendToBody only */
    /* before */
    beforeOpen: Function,
    beforeClose: Function
  },
  data: () => ({
    showPopper: false,
    triggerElm: null,
    popperInstance: null,
    popperOptions: {
      placement: 'bottom',
      computeStyle: {
        gpuAcceleration: false
      },
      modifiers: []
    }
  }),
  methods: {
    async doShow() {
      if (this.disabled) {
        this.showPopper = false;
        return;
      }

      if (typeof this.beforeOpen === 'function') {
        try {
          await this.beforeOpen();
        } catch(err) { console.error(err); }
      }
      this.showPopper = true;
    },
    async doToggle() {
      if (this.disabled) {
        this.showPopper = false;
        return;
      }
      
      if (this.showPopper) {
        this.showPopper = false;
      } else {
        if (typeof this.beforeOpen === 'function') {
          try {
            await this.beforeOpen();
          } catch(err) { console.error(err); }
        }
        this.showPopper = true;
      }
    },
    onMouseOver() {
      if (this.disabled) {
        this.showPopper = false;
        return;
      }

      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.showPopper = true;
      }, this.delayOnMouseOver);
    },
    onMouseOut() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.doClose();
      }, this.delayOnMouseOut);
    },
    async doClose() {
      if (typeof this.beforeClose === 'function') {
        try {
          await this.beforeClose();
        } catch(err) { console.error(err); }
      }
      if (this.trigger === 'hover' && this.$el.matches(':hover')) return;
      this.showPopper = false;
    },
    doDestroy() {
      /* remove child */
      const removeChild = (el, child) => {
        try {
          el.removeChild(child);
        } catch(err) {}
      }
      if (this.popperContent) {
        if (this.appendedToBody) {
          this.appendedToBody = false;
          if (this.popperOverlay) {
            removeChild(document.body, this.popperOverlay);
            this.popperOverlay = null;
          }
          removeChild(document.body, this.popperContent);
        } else {
          removeChild(this.$el, this.popperContent);
        }
      }
      /* destory popper instance */
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
        off(document, 'mousedown', this.escapeHandler);
      }
      this.$emit('content:destroyed');
    },
    select(val) {
      this.doClose();
      this.$emit('select', val);
    },
    /* handler */
    escapeHandler(evt) {
      if (
        !this.$el
        || !this.triggerElm
        || this.elementContains(this.$el, evt.target)
        || this.elementContains(this.triggerElm, evt.target)
        || !this.popperContent || this.elementContains(this.popperContent, evt.target)
        || (this.referenceId && commons.eventContainClassName(evt, this.referenceId))
      ) return;

      this.doClose();
    },
    clickHandler(event) {
      if (this.stopPropagation)
        event.stopPropagation()
      
      if (this.preventDefault) 
        event.preventDefault()
    },
    isNextFocusable(el) {
      if (el === document.body) {
        const elements = document.getElementsByClassName('popper-content');
        const lastElement = elements[elements.length - 1];
        if (lastElement === this.$refs.popperContent) {
          lastElement.focus();
          return true;
        }
      }
      return false;
    },
    keydownUp(event) {
      if (
        event.keyCode === 38 &&
        (this.elementContains(this.popperContent, event.target) || this.isNextFocusable(event.target))
      ) {
        this.$emit('content:keydown-up', {
          popperContentEl: this.$refs.popperContent
        });
      }
    },
    keydownDown(event) {
      if (
        event.keyCode === 40 &&
        (this.elementContains(this.popperContent, event.target) || this.isNextFocusable(event.target))
      ) {
        this.$emit('content:keydown-down', {
          popperContentEl: this.$refs.popperContent
        });
      }
    },
    keydownEnter(event) {
      if (
        event.keyCode === 13 &&
        (this.elementContains(this.popperContent, event.target) || this.isNextFocusable(event.target))
      ) {
        this.$emit('content:keydown-enter');
      }
    },
    elementContains(elm, otherElm) {
      if (typeof elm.contains === 'function') {
        return elm.contains(otherElm);
      }
      return false;
    },
    /* popper */
    createPopper() {
      this.popperContent = this.$refs.content
      /* trigger */
      switch (this.trigger) {
        case 'clickToOpen':
          on(document, 'mousedown', this.escapeHandler);
          break;
        case 'click':
        case 'clickToToggle':
          on(document, 'mousedown', this.escapeHandler);
          break;
        case 'hover':
          on(this.popperContent, 'mouseover', this.onMouseOver);
          on(this.popperContent, 'mouseout', this.onMouseOut);
          break;
        case 'focus':
          on(this.popperContent, 'focus', this.onMouseOver);
          on(this.popperContent, 'blur', this.onMouseOut);
          break;
      }
      /* append child */
      if (this.appendToBody) {
        this.appendedToBody = true;
        if (this.overlay) {
          this.popperOverlay = document.createElement('div');
          this.popperOverlay.style.position = 'fixed';
          this.popperOverlay.style.inset = 0;
          this.popperOverlay.style.overflow = 'hidden';
          this.popperOverlay.style.zIndex = 1060;
          this.popperOverlay.style.userSelect = 'none';
          this.referenceFor && this.popperOverlay.classList.add(this.referenceFor);
          document.body.appendChild(this.popperOverlay);
        }
        document.body.appendChild(this.popperContent);
      } else {
        this.$el.appendChild(this.popperContent);
      }
      /* placement */
      this.popperOptions = Object.assign(this.popperOptions, {
        placement: this.placement,
      });
      /* type Strategy = 'fixed' | 'absolute' */
      /* default: absolute */
      if (this.strategyFixed) 
        this.popperOptions.strategy = 'fixed';
      /* modifiers */
      this.popperOptions.modifiers = [
        {
          name: 'preventOverflow',
          options: {
            altAxis: this.preventOverflowAltAxis, // false by default
            tether: !this.preventOverflowTetherOff, // true by default
            padding: this.preventOverflowPadding
          },
        },
        {
          name: 'flip',
          enabled: this.flip
        },
        {
          name: 'offset',
          options: {
            offset: this.offset,
          }
        }
      ];
      /* type Boundary = 'clippingParents' | HTMLElement | Array<HTMLElement>; */
      /* default: clippingParents */
      if (this.preventOverflowBoundary && this.preventOverflowBoundary !== 'clippingParents') {
        try {
          const customBoundary = document.querySelector(`#${this.preventOverflowBoundary}`);
          let preventOverflow = this.popperOptions.modifiers.find(x => x.name === 'preventOverflow');
          preventOverflow.options.boundary = customBoundary;
        } catch(err) {
          console.warn(`[Warn] ${err}`);
        }
      }

      if (this.followWidth) {
        this.popperOptions.modifiers.push({
          name: 'width',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${this.$el.offsetWidth}px`;
          },
        });
      }

      /* set style */
      if (this.appendToBody || this.strategyFixed) {
        Object.assign(this.popperContent.style, {
          "max-height": "100%",
          "max-width": "100%",
          "display": "flex",
          "flex-direction": "column",
        });
        /* prevent overflow padding */
        if (this.preventOverflowPadding) {
          if (commons.isObject(this.preventOverflowPadding)) {
            let top = this.preventOverflowPadding.top ? this.preventOverflowPadding.top : 0,
                left= this.preventOverflowPadding.left ? this.preventOverflowPadding.left : 0,
                bottom = this.preventOverflowPadding.bottom ? this.preventOverflowPadding.bottom : 0,
                right = this.preventOverflowPadding.right ? this.preventOverflowPadding.right : 0;
            
            this.popperContent.style.maxWidth = `calc(100% - ${left + right}px)`;
            this.popperContent.style.maxHeight = `calc(100%) - ${top + bottom}px`;
          } else {
            this.popperContent.style.maxWidth = `calc(100% - ${this.preventOverflowPadding * 2}px)`;
            this.popperContent.style.maxHeight = `calc(100% - ${this.preventOverflowPadding * 2}px)`;
          }
        }
      }
      /* z-index */
      if (this.zIndex) {
        this.popperContent.style.zIndex = this.zIndex;
      }
      /* create popper */
      this.popperInstance = createPopper(this.triggerElm, this.popperContent, this.popperOptions);
      this.$emit('created', this);
    },
  },
  watch: {
    showPopper(value) {
      if (value) {      
        this.createPopper();
        this.$emit('show', this);
        on(document, 'keydown', this.keydownUp);
        on(document, 'keydown', this.keydownDown);
        on(document, 'keydown', this.keydownEnter);
      } else {
        this.$emit('hide', this);
        off(document, 'keydown', this.keydownUp);
        off(document, 'keydown', this.keydownDown);
        off(document, 'keydown', this.keydownEnter);
      }
    },
  },
  mounted() {
    this.triggerElm = this.$slots.reference[0].elm;
    /* add trigger event listener */
    switch (this.trigger) {
      case 'clickToOpen':
        on(this.triggerElm, 'click', this.doShow);
        break;
      case 'click': /* same as clickToToggle, provided for backwards compatibility. */
      case 'clickToToggle':
        on(this.triggerElm, 'click', this.doToggle);
        break;
      case 'hover':
        on(this.triggerElm, 'mouseover', this.onMouseOver);
        on(this.triggerElm, 'mouseout', this.onMouseOut);
        break;
      case 'focus':
        on(this.triggerElm, 'focus', this.onMouseOver);
        on(this.triggerElm, 'blur', this.onMouseOut);
        break;
    }
  },
  beforeDestroy() {
    this.doDestroy();
    /* remove trigger event listener */
    off(this.triggerElm, 'click', this.doToggle);
    off(this.triggerElm, 'mouseup', this.doClose);
    off(this.triggerElm, 'mousedown', this.doShow);
    off(this.triggerElm, 'focus', this.doShow);
    off(this.triggerElm, 'blur', this.doClose);
    off(this.triggerElm, 'mouseout', this.onMouseOut);
    off(this.triggerElm, 'mouseover', this.onMouseOver);
    off(document, 'keydown', this.keydownUp);
    off(document, 'keydown', this.keydownDown);
    off(document, 'keydown', this.keydownEnter);
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-popper {
  &.append-to-body {
    z-index: $z-index-popover;
  }
  &:not(.append-to-body) {
    z-index: $z-index-dropdown;
  }
}

.popper-content {
  max-height: 100%;
  max-width: 100%;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  box-shadow: var(--fts-elevation-3);
  border-radius: 0.25rem;
  background-color: var(--fts-popover-alt-background);
  color: var(--fts-text);
  font-size: 0.875rem;
}
</style>